// src/components/product/AttributeSelector/AttributeSelector.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Plus, X, ChevronDown, ChevronUp, Check, Search, Tag, Loader2 } from 'lucide-react';
import { getProductAttributes, createProductAttribute, createAttributeValue } from '../../../services/api/attributes';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { useAlert } from '../../../contexts/AlertContext';
import styles from './AttributeSelector.module.css';

const AttributeSelector = ({ 
  selectedAttributeIds = [], 
  selectedAttributeValueIds = [], 
  onAttributesChange, 
  onAttributeValuesChange,
  productType = 'simple',
  error = null 
}) => {
  const { t } = useLocalization();
  const { success, error: showError } = useAlert();
  
  // Selected attributes with their details
  const [selectedAttributes, setSelectedAttributes] = useState([]);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  
  // Create attribute form
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newAttributeName, setNewAttributeName] = useState('');
  const [newAttributeValues, setNewAttributeValues] = useState(['']);
  const [isCreating, setIsCreating] = useState(false);
  
  // Refs
  const searchInputRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowSearchDropdown(false);
        setShowCreateForm(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load selected attributes when selectedAttributeIds prop changes
  useEffect(() => {
    if (selectedAttributeIds.length > 0) {
      loadSelectedAttributes();
    } else {
      setSelectedAttributes([]);
    }
  }, [selectedAttributeIds]);

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchQuery.trim()) {
      searchTimeoutRef.current = setTimeout(() => {
        searchAttributes(searchQuery);
      }, 300);
    } else {
      setSearchResults([]);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  const loadSelectedAttributes = async () => {
    try {
      const attributeIds = selectedAttributeIds.map(attr => attr.attributeId);
      if (attributeIds.length === 0) {
        setSelectedAttributes([]);
        return;
      }

      // Load attributes with their values
      const response = await getProductAttributes({ 
        ids: attributeIds.join(','),
        include_values: true 
      });
      
      if (response.attributes) {
        // Filter to only include attributes that are actually selected
        const filteredAttributes = response.attributes.filter(attr => 
          attributeIds.includes(attr.id)
        );
        setSelectedAttributes(filteredAttributes);
      }
    } catch (error) {
      console.error('Failed to load selected attributes:', error);
    }
  };

  const searchAttributes = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setIsSearching(true);
      const response = await getProductAttributes({ 
        search: query.trim(),
        include_values: true,
        limit: 20 
      });
      
      // Filter out already selected attributes
      const selectedIds = selectedAttributeIds.map(attr => attr.attributeId);
      const filteredResults = (response.attributes || []).filter(
        attr => !selectedIds.includes(attr.id)
      );
      
      setSearchResults(filteredResults);
    } catch (error) {
      console.error('Failed to search attributes:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    if (value.trim() && !showSearchDropdown) {
      setShowSearchDropdown(true);
    }
  };

  const handleSearchInputFocus = () => {
    if (searchQuery.trim()) {
      setShowSearchDropdown(true);
    }
  };

  const handleAttributeSelect = async (attribute) => {
    try {
      // Add to selected attributes
      const newSelectedAttribute = {
        attributeId: attribute.id,
        usedForVariations: false
      };
      
      const newSelectedIds = [...selectedAttributeIds, newSelectedAttribute];
      onAttributesChange(newSelectedIds);
      
      // Clear search
      setSearchQuery('');
      setShowSearchDropdown(false);
      
      // Remove from search results
      setSearchResults(prev => prev.filter(attr => attr.id !== attribute.id));
      
      // Don't manually add to selectedAttributes - let useEffect handle it
      
    } catch (error) {
      console.error('Error selecting attribute:', error);
    }
  };

  const handleAttributeRemove = (attributeId) => {
    // Remove from selected attributes
    const newSelectedIds = selectedAttributeIds.filter(attr => attr.attributeId !== attributeId);
    onAttributesChange(newSelectedIds);
    
    // Remove all values for this attribute
    const attribute = selectedAttributes.find(attr => attr.id === attributeId);
    if (attribute) {
      const valueIdsToRemove = attribute.values.map(val => val.id);
      const newSelectedValueIds = selectedAttributeValueIds.filter(id => !valueIdsToRemove.includes(id));
      onAttributeValuesChange(newSelectedValueIds);
    }
    
    // Don't manually remove from selectedAttributes - let useEffect handle it
  };

  const handleVariationToggle = (attributeId, usedForVariations) => {
    const newSelectedIds = selectedAttributeIds.map(attr => 
      attr.attributeId === attributeId 
        ? { ...attr, usedForVariations }
        : attr
    );
    onAttributesChange(newSelectedIds);
  };

  const handleValueToggle = (valueId, isSelected) => {
    const newSelectedValueIds = isSelected 
      ? [...selectedAttributeValueIds, valueId]
      : selectedAttributeValueIds.filter(id => id !== valueId);
    
    onAttributeValuesChange(newSelectedValueIds);
  };

  const handleSelectAllValues = (attributeId) => {
    const attribute = selectedAttributes.find(attr => attr.id === attributeId);
    if (attribute) {
      const allValueIds = attribute.values.map(val => val.id);
      const otherValueIds = selectedAttributeValueIds.filter(id => 
        !allValueIds.includes(id)
      );
      onAttributeValuesChange([...otherValueIds, ...allValueIds]);
    }
  };

  const handleSelectNoneValues = (attributeId) => {
    const attribute = selectedAttributes.find(attr => attr.id === attributeId);
    if (attribute) {
      const valueIdsToRemove = attribute.values.map(val => val.id);
      const newSelectedValueIds = selectedAttributeValueIds.filter(id => 
        !valueIdsToRemove.includes(id)
      );
      onAttributeValuesChange(newSelectedValueIds);
    }
  };

  const handleCreateAttribute = async () => {
    if (!newAttributeName.trim() || newAttributeValues.filter(val => val.trim()).length === 0) {
      showError(t('attributes.nameRequired'));
      return;
    }

    try {
      setIsCreating(true);
      
      const attributeData = {
        name: newAttributeName.trim(),
        values: newAttributeValues.filter(val => val.trim()).map(val => val.trim())
      };

      const response = await createProductAttribute(attributeData);
      const newAttribute = response.attribute || response;
      
      success(t('attributes.attributeCreatedSuccess', { attributeName: newAttribute.name }));
      
      // Auto-select the new attribute
      const newSelectedAttribute = {
        attributeId: newAttribute.id,
        usedForVariations: false
      };
      
      onAttributesChange([...selectedAttributeIds, newSelectedAttribute]);
      // Don't manually add to selectedAttributes - let useEffect handle it
      
      // Reset form
      setShowCreateForm(false);
      setNewAttributeName('');
      setNewAttributeValues(['']);
      setShowSearchDropdown(false);
      
    } catch (error) {
      console.error('Error creating attribute:', error);
      showError(error.response?.data?.message || t('attributes.failedToCreate'));
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateValue = async (attributeId, newValue) => {
    if (!newValue?.trim()) {
      showError('Value name is required');
      return;
    }

    try {
      const response = await createAttributeValue(attributeId, { value: newValue.trim() });
      const createdValue = response.value || response;
      
      success(`Value "${createdValue.value}" created successfully!`);
      
      // Update the attribute in selectedAttributes
      setSelectedAttributes(prev => prev.map(attr => 
        attr.id === attributeId 
          ? { ...attr, values: [...attr.values, createdValue] }
          : attr
      ));
      
      // Auto-select the new value
      onAttributeValuesChange([...selectedAttributeValueIds, createdValue.id]);
      
    } catch (error) {
      console.error('Error creating value:', error);
      showError(error.response?.data?.message || 'Failed to create value');
    }
  };

  const addNewAttributeValue = () => {
    setNewAttributeValues(prev => [...prev, '']);
  };

  const removeAttributeValue = (index) => {
    setNewAttributeValues(prev => prev.filter((_, i) => i !== index));
  };

  const updateAttributeValue = (index, value) => {
    setNewAttributeValues(prev => prev.map((val, i) => i === index ? value : val));
  };

  const isAttributeUsedForVariations = (attributeId) => {
    const attr = selectedAttributeIds.find(attr => attr.attributeId === attributeId);
    return attr?.usedForVariations || false;
  };

  const getSelectedValuesCount = (attributeId) => {
    const attribute = selectedAttributes.find(attr => attr.id === attributeId);
    if (!attribute) return 0;
    
    const attributeValueIds = attribute.values.map(val => val.id);
    return selectedAttributeValueIds.filter(id => attributeValueIds.includes(id)).length;
  };

  const areAllValuesSelected = (attributeId) => {
    const attribute = selectedAttributes.find(attr => attr.id === attributeId);
    if (!attribute) return false;
    
    const attributeValueIds = attribute.values.map(val => val.id);
    return attributeValueIds.every(id => selectedAttributeValueIds.includes(id));
  };

  return (
    <div className={styles.attributeSelector} ref={dropdownRef}>
      {/* Search Input */}
      <div className={styles.searchSection}>
        <div className={styles.searchInputContainer}>
          <Search size={18} className={styles.searchIcon} />
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={handleSearchInputChange}
            onFocus={handleSearchInputFocus}
            placeholder="Search attributes to add..."
            className={styles.searchInput}
          />
          {isSearching && (
            <Loader2 size={16} className={styles.searchingIcon} />
          )}
        </div>

        {/* Search Results Dropdown */}
        {showSearchDropdown && (
          <div className={styles.searchDropdown}>
            {searchQuery.trim() === '' ? (
              <div className={styles.searchHint}>
                <Search size={16} />
                <span>Start typing to search attributes...</span>
              </div>
            ) : searchResults.length > 0 ? (
              <>
                <div className={styles.searchResultsHeader}>
                  <span>Found {searchResults.length} attributes</span>
                </div>
                <div className={styles.searchResults}>
                  {searchResults.map((attribute) => (
                    <div
                      key={attribute.id}
                      className={styles.searchResultItem}
                      onClick={() => handleAttributeSelect(attribute)}
                    >
                      <div className={styles.attributeInfo}>
                        <Tag size={16} className={styles.attributeIcon} />
                        <span className={styles.attributeName}>{attribute.name}</span>
                        <span className={styles.valueCount}>
                          {attribute.values?.length || 0} values
                        </span>
                      </div>
                      <Plus size={16} className={styles.addIcon} />
                    </div>
                  ))}
                </div>
              </>
            ) : !isSearching ? (
              <div className={styles.noResults}>
                <span>No attributes found for "{searchQuery}"</span>
              </div>
            ) : null}

            {/* Create New Attribute Option */}
            {searchQuery.trim() && !isSearching && (
              <div className={styles.createSection}>
                <button
                  type="button"
                  onClick={() => {
                    setNewAttributeName(searchQuery);
                    setShowCreateForm(true);
                    setShowSearchDropdown(false);
                  }}
                  className={styles.createFromSearchButton}
                >
                  <Plus size={16} />
                  Create attribute "{searchQuery}"
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create New Attribute Form */}
      {showCreateForm && (
        <div className={styles.createForm}>
          <div className={styles.createHeader}>
            <h4>Create New Attribute</h4>
            <button
              type="button"
              onClick={() => {
                setShowCreateForm(false);
                setNewAttributeName('');
                setNewAttributeValues(['']);
              }}
              className={styles.closeButton}
            >
              <X size={16} />
            </button>
          </div>
          
          <div className={styles.createContent}>
            <div className={styles.formGroup}>
              <label>Attribute Name</label>
              <input
                type="text"
                value={newAttributeName}
                onChange={(e) => setNewAttributeName(e.target.value)}
                placeholder="Enter attribute name (e.g., Color, Size)"
                className={styles.createInput}
              />
            </div>

            <div className={styles.formGroup}>
              <label>Values</label>
              {newAttributeValues.map((value, index) => (
                <div key={index} className={styles.valueInputGroup}>
                  <input
                    type="text"
                    value={value}
                    onChange={(e) => updateAttributeValue(index, e.target.value)}
                    placeholder={`Enter value ${index + 1}`}
                    className={styles.createInput}
                  />
                  {newAttributeValues.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeAttributeValue(index)}
                      className={styles.removeValueButton}
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
              
              <button
                type="button"
                onClick={addNewAttributeValue}
                className={styles.addValueButton}
              >
                <Plus size={14} />
                Add Value
              </button>
            </div>

            <div className={styles.createActions}>
              <button
                type="button"
                onClick={handleCreateAttribute}
                disabled={isCreating || !newAttributeName.trim()}
                className={styles.createButton}
              >
                {isCreating ? (
                  <div className={styles.spinner}></div>
                ) : (
                  <>
                    <Check size={14} />
                    Create Attribute
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Selected Attributes */}
      {selectedAttributes.length > 0 && (
        <div className={styles.selectedAttributesSection}>
          <h3>Selected Attributes ({selectedAttributes.length})</h3>
          
          <div className={styles.selectedAttributesList}>
            {selectedAttributes.map((attribute) => {
              const isUsedForVariations = isAttributeUsedForVariations(attribute.id);
              const selectedCount = getSelectedValuesCount(attribute.id);
              const allSelected = areAllValuesSelected(attribute.id);
              
              return (
                <SelectedAttributeCard
                  key={attribute.id}
                  attribute={attribute}
                  productType={productType}
                  isUsedForVariations={isUsedForVariations}
                  selectedValueIds={selectedAttributeValueIds}
                  selectedCount={selectedCount}
                  allSelected={allSelected}
                  onRemove={() => handleAttributeRemove(attribute.id)}
                  onVariationToggle={(usedForVariations) => 
                    handleVariationToggle(attribute.id, usedForVariations)
                  }
                  onValueToggle={handleValueToggle}
                  onSelectAll={() => handleSelectAllValues(attribute.id)}
                  onSelectNone={() => handleSelectNoneValues(attribute.id)}
                  onCreateValue={(newValue) => handleCreateValue(attribute.id, newValue)}
                />
              );
            })}
          </div>
        </div>
      )}

      {error && <span className={styles.errorText}>{error}</span>}
    </div>
  );
};

// Selected Attribute Card Component
const SelectedAttributeCard = ({
  attribute,
  productType,
  isUsedForVariations,
  selectedValueIds,
  selectedCount,
  allSelected,
  onRemove,
  onVariationToggle,
  onValueToggle,
  onSelectAll,
  onSelectNone,
  onCreateValue
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [showCreateValue, setShowCreateValue] = useState(false);
  const [newValueName, setNewValueName] = useState('');
  const [isCreatingValue, setIsCreatingValue] = useState(false);

  const handleCreateValue = async () => {
    if (!newValueName.trim()) return;
    
    setIsCreatingValue(true);
    try {
      await onCreateValue(newValueName);
      setNewValueName('');
      setShowCreateValue(false);
    } finally {
      setIsCreatingValue(false);
    }
  };

  return (
    <div className={styles.selectedAttributeCard}>
      <div className={styles.attributeCardHeader}>
        <div className={styles.attributeInfo}>
          <Tag size={16} className={styles.attributeIcon} />
          <span className={styles.attributeName}>{attribute.name}</span>
          <span className={styles.valueCount}>
            {selectedCount}/{attribute.values.length} selected
          </span>
        </div>
        
        <div className={styles.attributeControls}>
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className={styles.expandButton}
          >
            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          
          <button
            type="button"
            onClick={onRemove}
            className={styles.removeAttributeButton}
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className={styles.valuesSection}>
          <AttributeValuesSelector
            attribute={attribute}
            selectedValueIds={selectedValueIds}
            onValueToggle={onValueToggle}
            onSelectAll={onSelectAll}
            onSelectNone={onSelectNone}
            onCreateValue={onCreateValue}
            selectedCount={selectedCount}
            allSelected={allSelected}
          />
          
          {/* Used for variations checkbox at the bottom */}
          {productType === 'variable' && (
            <div className={styles.variationSection}>
              <label className={styles.variationCheckbox}>
                <input
                  type="checkbox"
                  checked={isUsedForVariations}
                  onChange={(e) => onVariationToggle(e.target.checked)}
                />
                <span className={styles.variationLabel}>
                  Used for product variations
                </span>
              </label>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Attribute Values Selector Component (similar to TagSelector)
const AttributeValuesSelector = ({
  attribute,
  selectedValueIds,
  onValueToggle,
  onSelectAll,
  onSelectNone,
  onCreateValue,
  selectedCount,
  allSelected
}) => {
  const { t } = useLocalization();
  const { success, error: showError } = useAlert();
  
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newValueName, setNewValueName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [createValueError, setCreateValueError] = useState('');
  
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const createInputRef = useRef(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setShowCreateForm(false);
        setNewValueName('');
        setCreateValueError('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current && !showCreateForm) {
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 100);
    }
  }, [isOpen, showCreateForm]);

  // Focus create input when create form shows
  useEffect(() => {
    if (showCreateForm && createInputRef.current) {
      setTimeout(() => {
        createInputRef.current.focus();
      }, 100);
    }
  }, [showCreateForm]);

  const handleValueSelect = (valueId) => {
    onValueToggle(valueId, !selectedValueIds.includes(valueId));
  };

  const handleValueRemove = (valueId) => {
    onValueToggle(valueId, false);
  };

  const handleSelectorClick = () => {
    setIsOpen(!isOpen);
  };

  const handleCreateNewClick = () => {
    setShowCreateForm(true);
    setSearchTerm('');
    setCreateValueError('');
  };

  const handleCancelCreate = () => {
    setShowCreateForm(false);
    setNewValueName('');
    setCreateValueError('');
  };

  const handleCreateValue = async () => {
    if (!newValueName.trim()) {
      setCreateValueError('Value name is required');
      return;
    }

    // Check if value already exists
    const existingValue = attribute.values.find(value => 
      value.value.toLowerCase() === newValueName.trim().toLowerCase()
    );
    if (existingValue) {
      setCreateValueError('This value already exists');
      return;
    }

    setIsCreating(true);
    setCreateValueError('');

    try {
      await onCreateValue(newValueName);
      setNewValueName('');
      setShowCreateForm(false);
    } catch (error) {
      setCreateValueError('Failed to create value');
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCreateValue();
    } else if (e.key === 'Escape') {
      handleCancelCreate();
    }
  };

  const filteredValues = attribute.values.filter(value =>
    value.value.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedValues = attribute.values.filter(value => 
    selectedValueIds.includes(value.id)
  );

  // Check if search term could be a new value
  const canCreateFromSearch = searchTerm.trim() && 
    !filteredValues.some(value => 
      value.value.toLowerCase() === searchTerm.toLowerCase()
    );

  return (
    <div className={styles.attributeValuesSelector} ref={dropdownRef}>
      <div className={styles.valuesSectionHeader}>
        <span>Attribute Values</span>
        <div className={styles.bulkActions}>
          <button
            type="button"
            onClick={onSelectAll}
            disabled={allSelected}
            className={styles.bulkActionButton}
          >
            Select All
          </button>
          <button
            type="button"
            onClick={onSelectNone}
            disabled={selectedCount === 0}
            className={styles.bulkActionButton}
          >
            Select None
          </button>
        </div>
      </div>

      <div 
        className={`${styles.valuesSelectorInput} ${isOpen ? styles.open : ''}`}
        onClick={handleSelectorClick}
      >
        <div className={styles.selectedContent}>
          {selectedValues.length > 0 ? (
            <div className={styles.selectedValues}>
              {selectedValues.map((value) => (
                <div key={value.id} className={styles.selectedValue}>
                  <span className={styles.valueText}>{value.value}</span>
                  <button
                    type="button"
                    className={styles.removeValueButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleValueRemove(value.id);
                    }}
                  >
                    <X size={10} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <span className={styles.placeholder}>Select values...</span>
          )}
        </div>
        <ChevronDown 
          size={16} 
          className={`${styles.chevron} ${isOpen ? styles.rotated : ''}`} 
        />
      </div>

      {isOpen && (
        <div className={styles.valuesDropdown}>
          {!showCreateForm ? (
            <>
              {/* Search Input */}
              <div className={styles.searchContainer}>
                <Search size={14} className={styles.searchIcon} />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search values..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={styles.searchInput}
                />
              </div>

              {/* Create New Value from Search */}
              {canCreateFromSearch && (
                <div className={styles.createFromSearch}>
                  <button
                    type="button"
                    className={styles.createFromSearchButton}
                    onClick={() => {
                      setNewValueName(searchTerm);
                      setShowCreateForm(true);
                    }}
                  >
                    <Plus size={14} />
                    <span>Create value "{searchTerm}"</span>
                  </button>
                </div>
              )}

              {/* Values List */}
              <div className={styles.valuesList}>
                {filteredValues.length > 0 ? (
                  filteredValues.map((value) => (
                    <ValueItem
                      key={value.id}
                      value={value}
                      isSelected={selectedValueIds.includes(value.id)}
                      onToggle={() => handleValueSelect(value.id)}
                    />
                  ))
                ) : (
                  <div className={styles.emptyState}>
                    {searchTerm ? `No values found for "${searchTerm}"` : 'No values available'}
                  </div>
                )}
              </div>

              {/* Create New Value Button */}
              <div className={styles.createNewSection}>
                <button
                  type="button"
                  className={styles.createNewButton}
                  onClick={handleCreateNewClick}
                >
                  <Plus size={14} />
                  Add new value
                </button>
              </div>
            </>
          ) : (
            /* Create Value Form */
            <div className={styles.createValueForm}>
              <div className={styles.createFormHeader}>
                <h4>Add New Value</h4>
                <button
                  type="button"
                  className={styles.createFormClose}
                  onClick={handleCancelCreate}
                >
                  <X size={14} />
                </button>
              </div>
              
              <div className={styles.createForm}>
                <div className={styles.createFormGroup}>
                  <label htmlFor="newValueName">Value Name *</label>
                  <input
                    ref={createInputRef}
                    type="text"
                    id="newValueName"
                    value={newValueName}
                    onChange={(e) => {
                      setNewValueName(e.target.value);
                      setCreateValueError('');
                    }}
                    onKeyDown={handleCreateInputKeyDown}
                    placeholder="Enter value name"
                    className={createValueError ? styles.errorInput : ''}
                    disabled={isCreating}
                    maxLength={100}
                  />
                  {createValueError && (
                    <span className={styles.createErrorText}>{createValueError}</span>
                  )}
                </div>
                
                <div className={styles.createFormActions}>
                  <button
                    type="button"
                    className={styles.createCancelButton}
                    onClick={handleCancelCreate}
                    disabled={isCreating}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className={styles.createSubmitButton}
                    onClick={handleCreateValue}
                    disabled={isCreating || !newValueName.trim()}
                  >
                    {isCreating ? (
                      <>
                        <div className={styles.createLoadingSpinner}></div>
                        Adding...
                      </>
                    ) : (
                      <>
                        <Plus size={14} />
                        Add Value
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Value Item Component
const ValueItem = ({ value, isSelected, onToggle }) => {
  return (
    <div 
      className={`${styles.valueItem} ${isSelected ? styles.selected : ''}`}
      onClick={onToggle}
    >
      <div className={styles.valueContent}>
        <span className={styles.valueName}>{value.value}</span>
      </div>
      
      <div className={styles.checkbox}>
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onToggle}
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    </div>
  );
};

export default AttributeSelector;